import {isNullOrUndefined} from "util";
import ReminderNotification from "../model/notification/reminder/ReminderNotification";
import * as moment from "moment";
import {store} from "../navigation/ReduxProvider";
import {notificationsFetched, triggerReminderNotification} from "../store/action/notification/notifications";
import LeadStatusNotification from "../model/notification/lead/LeadStatusNotification";
import AssigneeChangeNotification from "../model/notification/assignee/AssigneeChangeNotification";
import LeadWatcherChangeNotification from "../model/notification/watcher/LeadWatcherChangeNotification";
import {fetchLead, fetchLeadEvents, leadAgentUpdated, leadStatusUpdated, leadWatchersUpdated} from "../store/action/lead/lead";
import NewThirdPartyLeadNotification from "../model/notification/thirdPartyLead/NewThirdPartyLeadNotification";
import {fetchIndividualContacts} from "../store/action/contacts/contacts";
import {getLeadByIdOrRefNo} from "../store/view/lead";
import {fetchUsers} from "../store/action/user/user";
import Executor from "../store/action/Executor";
import SmsNotification from "../model/notification/sms/SmsNotification";
import {NotificationType} from "../model/notification/NotificationBase";
import {fetchTextMessages} from "../store/action/sms/sms";
import QuoteCommentNotification from "../model/notification/quote/QuoteCommentNotification";
import QuoteStatusChangedNotification from "../model/notification/quote/QuoteStatusChangedNotification";
import UserMentionNotification from "../model/notification/mention/UserMentionNotification";
import {api} from "../api/api";
import JobUserMentionNotification from "../model/notification/mention/JobUserMentionNotification";
import JobReminderNotification from "../model/notification/reminder/JobReminderNotification";
import JobStatusNotification from "../model/notification/job/JobStatusNotification";
import {fetchJob, fetchJobEvents, jobAgentUpdated, jobWatchersUpdated, statusUpdated} from "../store/action/job/job";
import {getJobByIdOrRefNumber} from "../store/view/job";
import LeadAgentChangeNotification from "../model/notification/assignee/LeadAgentChangeNotification";
import JobAgentChangeNotification from "../model/notification/assignee/job/JobAgentChangeNotification";
import JobWatcherChangeNotification from "../model/notification/watcher/JobWatcherChangeNotification";
import JobCreatedFromQuoteNotification from "../model/notification/job/JobCreatedFromQuoteNotification";
import {fetchSites} from "../store/action/site/site";
import {fetchJobTags} from "../store/action/tag/tags";
import PaymentArrivedNotification from "../model/notification/payment/PaymentArrivedNotification";
import BillCreatedNotification from "../model/notification/invoice/BillCreatedNotification";
import InvoiceCreatedNotification from "../model/notification/invoice/InvoiceCreatedNotification";
import BillPaidNotification from "../model/notification/invoice/BillPaidNotification";
import InvoicePaidNotification from "../model/notification/invoice/InvoicePaidNotification";

/**
 * Notifications service.
 * Fetch notifications from server and trigger it when it's fire time passed.
 */
class NotificationService {

	/**
	 * Pulling timer identifier.
	 * @type {number}
	 */
	private pullingTimerId: number = null;
	/**
	 * Date of last pull notifications from server.
	 */
	private lastPullDate: moment.Moment;

	constructor() {
		this.pullNotifications = this.pullNotifications.bind(this);
	}

	/**
	 * Starts pulling notifications from server.
	 * Clear previous timers.
	 */
	startPulling() {
		this.clearTimers();
		this.pullingTimerId = window.setInterval(this.pullNotifications, 15 * 1000);
		this.lastPullDate = moment();
		api.instance.notificationApi.checkIfAnyNotificationIsUnread(this.lastPullDate)
	}

	/**
	 * Stop pulling notifications.
	 * Clears previous timers.
	 */
	stopPulling() {
		this.clearTimers();
	}

	/**
	 * Clear timers.
	 */
	private clearTimers() {
		if (!isNullOrUndefined(this.pullingTimerId)) {
			clearInterval(this.pullingTimerId);
		}
		this.lastPullDate = null;
	}

	/**
	 * Pull notifications from server.
	 */
	private pullNotifications() {
		const time = moment();
		api.instance.notificationApi.fetchNewNotifications(this.lastPullDate, time).then(
			(notifications: Array<NotificationType>) => {
				notifications.forEach((notification) => this.triggerNotification(notification, time));
				const filteredNotifications = notifications.filter(n => n.visibleFrom.isBetween(this.lastPullDate, time));
				if (filteredNotifications.length) {
					store.dispatch(notificationsFetched(filteredNotifications))
				}
				this.lastPullDate = time;
			}).catch(() => {});
		api.instance.notificationApi.checkIfAnyNotificationIsUnread(time)
	}

	/**
	 * Trigger notification.
	 * Check if notification arrived since last notification pull action.
	 * If notification fire date was between <LAST_NOTIFICATION_PULL_DATE; CURRENT_TIME> trigger it.
	 * @param {ReminderNotification} notification notification to show.
	 * @param {moment.Moment} currentTime current time. This is needed to have the same time for every notification check.
	 */
	private triggerNotification(notification: NotificationType, currentTime: moment.Moment) {
		if (notification.visibleFrom.isBetween(this.lastPullDate, currentTime)) {
			if (notification instanceof LeadStatusNotification) {
				store.dispatch(leadStatusUpdated(notification.data.leadId, notification.data.currentStatusId));
				store.dispatch(fetchLeadEvents(notification.data.leadId, () => {}, () => {}));
				store.dispatch(triggerReminderNotification(notification));
			}
			else if (notification instanceof LeadAgentChangeNotification) {
				store.dispatch(leadAgentUpdated(notification.data.leadId, notification.data.currentAgentId));

				const e = new Executor(triggerReminderNotification(notification));
				const actions = [
					fetchLeadEvents(notification.data.leadId, () => {e.actionSuccess()}, () => {}),
					fetchUsers(() => {e.actionSuccess()}, () => {})
				];
				const notificationLead = getLeadByIdOrRefNo(notification.data.leadId, store.getState());
				if (notificationLead.isEmpty) {
					actions.push(fetchLead(notification.data.leadId, () => {e.actionSuccess()}, () => {}));
				}
				e.setActions(actions).run();
			}
			else if (notification instanceof AssigneeChangeNotification) {
				store.dispatch(leadAgentUpdated(notification.data.leadId, notification.data.agentId));

				const e = new Executor(triggerReminderNotification(notification));
				const actions = [
					fetchLeadEvents(notification.data.leadId, () => {e.actionSuccess()}, () => {}),
					fetchUsers(() => {e.actionSuccess()}, () => {})
				];
				const notificationLead = getLeadByIdOrRefNo(notification.data.leadId, store.getState());
				if (notificationLead.isEmpty) {
					actions.push(fetchLead(notification.data.leadId, () => {e.actionSuccess()}, () => {}));
				}
				e.setActions(actions).run();
			}
			else if (notification instanceof LeadWatcherChangeNotification) {
				store.dispatch(leadWatchersUpdated(notification.data.leadId, notification.data.currentWatchersIds));

				const e = new Executor(triggerReminderNotification(notification));
				const actions = [
					fetchLeadEvents(notification.data.leadId, () => {e.actionSuccess()}, () => {}),
					fetchUsers(() => {e.actionSuccess()}, () => {})
				];
				const notificationLead = getLeadByIdOrRefNo(notification.data.leadId, store.getState());
				if (notificationLead.isEmpty) {
					actions.push(fetchLead(notification.data.leadId, () => {e.actionSuccess()}, () => {}));
				}
				e.setActions(actions).run();
			}
			else if (notification instanceof NewThirdPartyLeadNotification) {
				store.dispatch(
					fetchLead(notification.data.leadId, () => {store.dispatch(triggerReminderNotification(notification))},
						() => {}));
				store.dispatch(fetchIndividualContacts(() => {}, () => {}));
				store.dispatch(fetchSites(() => {}, () => {}));
				return;
			}
			else if (notification instanceof SmsNotification) {
				notification.data.recognizedSmsData.map(rsd => {
					store.dispatch(fetchLead(rsd.leadId, () => {}, () => {}));
					store.dispatch(fetchLeadEvents(rsd.leadId, () => {}, () => {}));
				});
				store.dispatch(fetchTextMessages(() => {}, () => {}));
				store.dispatch(triggerReminderNotification(notification));
			}
			else if (notification instanceof QuoteCommentNotification) {
				store.dispatch(fetchLead(notification.data.leadId, () => {}, () => {}));
				store.dispatch(fetchLeadEvents(notification.data.leadId, () => {}, () => {}));
				store.dispatch(triggerReminderNotification(notification));
			}
			else if (notification instanceof QuoteStatusChangedNotification) {
				store.dispatch(fetchLead(notification.data.leadId, () => {}, () => {}));
				store.dispatch(fetchLeadEvents(notification.data.leadId, () => {}, () => {}));
				store.dispatch(triggerReminderNotification(notification));
			}
			else if (notification instanceof UserMentionNotification) {
				store.dispatch(triggerReminderNotification(notification));
			}
			else if (notification instanceof ReminderNotification) {
				store.dispatch(triggerReminderNotification(notification));
			}
			else if (notification instanceof JobStatusNotification) {
				store.dispatch(statusUpdated(notification.data.jobId, notification.data.currentStatusId));
				store.dispatch(fetchJobEvents(notification.data.jobId, () => {}, () => {}));
				store.dispatch(triggerReminderNotification(notification));
			}
			else if (notification instanceof JobReminderNotification) {
				store.dispatch(triggerReminderNotification(notification));
			}
			else if (notification instanceof JobAgentChangeNotification) {
				store.dispatch(jobAgentUpdated(notification.data.jobId, notification.data.currentAgentId));

				const e = new Executor(triggerReminderNotification(notification));
				const actions = [
					fetchJobEvents(notification.data.jobId, () => {e.actionSuccess()}, () => {}),
					fetchUsers(() => {e.actionSuccess()}, () => {})
				];
				const notificationJob = getJobByIdOrRefNumber(notification.data.jobId, store.getState());
				if (notificationJob.isEmpty) {
					actions.push(fetchJob(notification.data.jobId, () => {e.actionSuccess()}, () => {}));
				}
				e.setActions(actions).run();
			}
			else if (notification instanceof JobCreatedFromQuoteNotification) {
				const e = new Executor(triggerReminderNotification(notification));

				const actions = [
					fetchJob(notification.data.jobId, () => {}, () => {}),
					fetchJobTags(() => {}, () => {})];

				e.setActions(actions).run();
			}
			else if (notification instanceof JobWatcherChangeNotification) {
				store.dispatch(jobWatchersUpdated(notification.data.jobId, notification.data.currentWatchersIds));

				const e = new Executor(triggerReminderNotification(notification));
				const actions = [
					fetchJobEvents(notification.data.jobId, () => {e.actionSuccess()}, () => {}),
					fetchUsers(() => {e.actionSuccess()}, () => {})
				];
				const notificationJob = getJobByIdOrRefNumber(notification.data.jobId, store.getState());
				if (notificationJob.isEmpty) {
					actions.push(fetchJob(notification.data.jobId, () => {e.actionSuccess()}, () => {}));
				}
				e.setActions(actions).run();
			}
			else if (notification instanceof JobUserMentionNotification) {
				store.dispatch(triggerReminderNotification(notification));
			}
		}
	}
}

const instance = new NotificationService();
export default instance;
